import {artifactMembershipNiceName} from '@wandb/weave/common/util/artifacts';
import {Button} from '@wandb/weave/components';
import * as Dialog from '@wandb/weave/components/Dialog';
import {Icon} from '@wandb/weave/components/Icon';
import React, {FC, useCallback, useState} from 'react';
import {toast} from 'react-toastify';

import * as Generated from '../../../../generated/graphql';
import {
  extractStatusCodeFromApolloError,
  propagateErrorsContext,
} from '../../../../util/errors';
import {Banner, BannerFlexWrapper, BannerTextWrapper} from '../../../Banner';

const getErrorMessage = (error: unknown) => {
  const statusCode = extractStatusCodeFromApolloError(error);
  const message = (error as any)?.result?.errors[0]?.message;
  if (statusCode === 400 && message.contains('existing alias')) {
    return 'This artifact cannot be deleted because of an existing alias.';
  }

  return 'Something went wrong while trying to delete this artifact.';
};

type DeleteMembershipModalProps = {
  open: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onClose: () => void;
  isPortfolio: boolean;
  artifactName: string;
  versionIndex: number;
  hasAliases: boolean;
  hasMemberships: boolean;
  afterDelete: () => void;
  artifactId: string;
  artifactVersionId: string;
  artifactIsLinkedToGlobalRegistry?: boolean;
};

export const DeleteMembershipModal: FC<DeleteMembershipModalProps> = ({
  open,
  setIsOpen,
  onClose,
  isPortfolio,
  artifactName,
  versionIndex,
  hasAliases,
  hasMemberships,
  afterDelete,
  artifactId,
  artifactVersionId,
  artifactIsLinkedToGlobalRegistry,
}) => {
  const [isDeleting, setDeleting] = useState(false);
  const [deleteArtifact] = Generated.useDeleteArtifactMutation({
    context: propagateErrorsContext(),
  });
  const [unlinkArtifact] = Generated.useUnlinkArtifactMutation({
    context: propagateErrorsContext(),
  });

  const onDeleteClick = useCallback(async () => {
    setDeleting(true);

    try {
      if (isPortfolio) {
        await unlinkArtifact({
          variables: {
            artifactPortfolioID: artifactId ?? '',
            artifactID: artifactVersionId,
          },
        });
      } else {
        await deleteArtifact({
          variables: {
            artifactID: artifactVersionId,
            deleteAliases: true,
          },
          context: propagateErrorsContext(),
        });
      }
      toast(
        `Artifact ${artifactName}:v${versionIndex} ${
          isPortfolio ? 'unlinked' : 'deleted'
        }.`,
        {type: 'success'}
      );
      afterDelete();
    } catch (error) {
      console.error(error);
      if (isPortfolio && extractStatusCodeFromApolloError(error) === 403) {
        toast(
          'You must be a registry admin to unlink a version with protected aliases.'
        );
      } else {
        toast(getErrorMessage(error));
      }
      onClose();
    }
    setDeleting(false);
  }, [
    afterDelete,
    artifactId,
    artifactVersionId,
    deleteArtifact,
    isPortfolio,
    unlinkArtifact,
    onClose,
    artifactName,
    versionIndex,
  ]);

  return (
    <Dialog.Root open={open} onOpenChange={setIsOpen}>
      <Dialog.Portal>
        <Dialog.Overlay />
        <Dialog.Content
          className="w-[600px] rounded-lg"
          onInteractOutside={e => {
            e.preventDefault();
          }}>
          <Dialog.Title>
            {isPortfolio ? 'Unlink' : 'Delete'} this artifact?
          </Dialog.Title>
          <Dialog.Description className="pt-16" data-test="delete-summary">
            {isPortfolio
              ? 'Any references to this linked version will be broken.'
              : `Artifact "${artifactMembershipNiceName(artifactName, {
                  versionIndex,
                })}" will be scheduled for deletion. Any references to this artifact will be broken.`}
          </Dialog.Description>
          <Dialog.Description>
            {artifactIsLinkedToGlobalRegistry && !isPortfolio && (
              <Banner variant="error" className="mt-16">
                <BannerFlexWrapper>
                  <div>
                    <Icon name="warning" />
                  </div>
                  <BannerTextWrapper>
                    This artifact is linked to a registry, from which it will
                    also be deleted. References to linked versions will also be
                    broken.
                  </BannerTextWrapper>
                </BannerFlexWrapper>
              </Banner>
            )}
          </Dialog.Description>
          <div className="mb-8 mt-24 flex items-center">
            <Button
              data-cy="artifact-delete-confirm"
              variant="destructive"
              disabled={isDeleting}
              size="large"
              onClick={onDeleteClick}>
              Delete
            </Button>
            <Dialog.Close className="ml-8" asChild>
              <Button
                className="dark:text-moon-200/100 dark:filter-none hover:dark:text-moon-200"
                variant="ghost"
                size="large"
                disabled={isDeleting}>
                Cancel
              </Button>
            </Dialog.Close>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
